import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from './AuthContext';

const ApiPromptsContext = createContext();

export const ApiPromptsProvider = ({ children }) => {
  const [remainingPrompts, setRemainingPrompts] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser, token } = useAuth();

  const fetchRemainingPrompts = useCallback(async () => {
    if (!currentUser || !token) {
      setRemainingPrompts(3);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/ai-images/remaining-prompts');
      setRemainingPrompts(response.data.remainingPrompts);
      setError(null);
    } catch (error) {
      console.error('Kalan kullanım hakkı getirilemedi:', error);
      setError('Kalan kullanım hakkı getirilemedi');
      setRemainingPrompts(3);
    } finally {
      setLoading(false);
    }
  }, [currentUser, token]);

  const generateDesign = useCallback(async (prompt) => {
    if (!prompt || !prompt.trim()) {
      toast.error('Lütfen bir tasarım açıklaması girin.');
      return { success: false, error: 'Lütfen bir tasarım açıklaması girin.' };
    }
    
    if (remainingPrompts <= 0) {
      toast.error('Günlük kullanım hakkınız doldu. Yarın tekrar deneyin.');
      return { success: false, error: 'Günlük kullanım hakkınız doldu. Yarın tekrar deneyin.' };
    }

    try {
      setLoading(true);
      const response = await api.post('/ai-images/generate', { prompt: prompt.trim() });
      
      setRemainingPrompts(response.data.remainingPrompts);
      setError(null);
      
      return {
        success: true,
        imageUrl: response.data.imageUrl,
        remainingPrompts: response.data.remainingPrompts
      };
    } catch (err) {
      console.error('Tasarım oluşturma hatası:', err);
      
      let errorMessage = 'Tasarım oluşturulurken bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      
      if (err.response?.status === 429) {
        errorMessage = 'Çok fazla istek gönderildi. Lütfen biraz bekleyip tekrar deneyin.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [remainingPrompts]);

  return (
    <ApiPromptsContext.Provider value={{
      remainingPrompts,
      loading,
      error,
      fetchRemainingPrompts,
      generateDesign
    }}>
      {children}
    </ApiPromptsContext.Provider>
  );
};

export const useApiPrompts = () => {
  const context = useContext(ApiPromptsContext);
  if (!context) {
    throw new Error('useApiPrompts hook must be used within an ApiPromptsProvider');
  }
  return context;
}; 